/**
 *  @class
 *  @function FreeShipping
 */

if (!customElements.get('free-shipping')) {
  class FreeShipping extends HTMLElement {
    constructor() {
      super();
      this.apiEndpoint = '/apps/dtf-upload/thresholds';
      this.perfTimeoutMs = 3500;
      
      this.cache = new Map();
      this.cacheExpiry = 5 * 60 * 1000;
    }

    connectedCallback() {
      this.amountTextSpan = this.querySelector('.free-shipping--text span');
      this.remainingText = this.querySelector('.free-shipping--text-remaining');
      this.fullText = this.querySelector('.free-shipping--text-full');

      this.renderDynamic().catch(() => {
        this.hide();
      });
    }

    async renderDynamic() {
      const cart = await this.fetchWithTimeout('/cart.js', { credentials: 'same-origin' });
      const cartJson = await cart.json();

      if (!cartJson || !Array.isArray(cartJson.items) || cartJson.items.length === 0) {
        this.renderStatic(0, this.getFallbackMinimum());
        return;
      }
      
      const payload = {
        currency: Shopify.currency && Shopify.currency.active,
        items: cartJson.items.map((it) => ({ 
          variant_id: it.variant_id, 
          quantity: it.quantity,
          price: it.final_price || it.price
        }))
      };

      const cacheKey = JSON.stringify(payload);
      const now = Date.now();
      
      const cached = this.cache.get(cacheKey);
      if (cached && (now - cached.timestamp) < this.cacheExpiry) {
        this.renderProfiles(cached.data.profiles, cached.data.currency);
        return;
      }

      const resp = await this.fetchWithTimeout(this.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      });

      if (!resp.ok) throw new Error('thresholds_api_failed');
      const data = await resp.json();
      
      this.cache.set(cacheKey, {
        data: data,
        timestamp: now
      });
      
      if (this.cache.size > 10) {
        this.cleanCache();
      }

      if (!data || !Array.isArray(data.profiles)) {
        throw new Error('thresholds_empty');
      }

      const validProfiles = data.profiles.filter((p) => parseInt(p.threshold_cents || 0, 10) > 0);
      if (validProfiles.length === 0) {
        this.hide();
        return;
      }

      this.renderProfiles(validProfiles, data.currency || (Shopify.currency && Shopify.currency.active));
    }

    renderProfiles(profiles, currency) {
      const bar = this.querySelector('.free-shipping--bar');
      const container = bar ? bar.parentElement : this;

      container.querySelectorAll('.free-shipping__profile').forEach((el) => el.remove());

      if (this.remainingText) this.remainingText.style.display = 'none';
      if (this.fullText) this.fullText.style.display = 'none';

      const staticTextContainer = this.querySelector('.free-shipping--text');
      if (staticTextContainer) {
        staticTextContainer.style.display = 'none';
      }

      const sortedProfiles = profiles.sort((a, b) => {
        const aName = (a.name || '').toLowerCase();
        const bName = (b.name || '').toLowerCase();
        
        if (aName.includes('dtf') && aName.includes('transfer') && !bName.includes('dtf')) {
          return -1;
        }
        if (bName.includes('dtf') && bName.includes('transfer') && !aName.includes('dtf')) {
          return 1;
        }
        
        if (aName.includes('blank') && aName.includes('apparel') && !bName.includes('blank')) {
          return 1;
        }
        if (bName.includes('blank') && bName.includes('apparel') && !aName.includes('blank')) {
          return -1;
        }
        
        return aName.localeCompare(bName);
      });

      sortedProfiles.forEach((p) => {
        const threshold = parseInt(p.threshold_cents || 0, 10);
        const subtotal = parseInt(p.bucket_subtotal_cents || 0, 10);
        if (!(threshold > 0)) return;

        const percentage = Math.max(0, Math.min(1, subtotal / threshold));
        const remaining = Math.max(0, threshold - subtotal);

        const wrap = document.createElement('div');
        wrap.className = 'free-shipping__profile';

        const title = document.createElement('div');
        title.className = 'free-shipping__profile-title';
        title.style.display = 'flex';
        title.style.justifyContent = 'space-between';
        title.style.alignItems = 'center';
        title.style.marginBottom = '6px';

        const left = document.createElement('div');
        left.textContent = p.name || 'Shipping';
        left.style.fontWeight = '600';
        left.style.fontSize = '0.9rem';

        const right = document.createElement('div');
        right.style.fontSize = '0.85rem';
        if (subtotal < threshold) {
          const fmt = window.theme && window.theme.settings && window.theme.settings.money_with_currency_format || "${{amount}}";
          right.innerHTML = this.formatMoney(remaining, fmt) + ' remaining';
        } else {
          right.textContent = 'You earned free shipping!';
        }
        title.appendChild(left);
        title.appendChild(right);

        const barOuter = document.createElement('div');
        barOuter.className = 'free-shipping--bar';
        const barInner = document.createElement('div');
        barInner.className = 'free-shipping--percentage';
        barInner.style.transform = `scale(${percentage}, 1)`;
        
        if (subtotal >= threshold) {
          barInner.style.backgroundColor = '#ecb317';
          barOuter.style.borderColor = '#ecb317';
          barOuter.classList.add('completed');
          barInner.classList.add('completed');
        }

        barOuter.appendChild(barInner);
        wrap.appendChild(title);
        wrap.appendChild(barOuter);
        container.appendChild(wrap);
      });
    }

    hide() {
      this.style.display = 'none';
    }

    renderStatic(total, minimum) {
      let percentage = 1;
      if (total < minimum) {
        percentage = total / minimum;

        if (this.amountTextSpan) {
          const remaining = minimum - total;
          const format = (window.theme && window.theme.settings && window.theme.settings.money_with_currency_format) || "${{amount}}";
          this.amountTextSpan.innerHTML = this.formatMoney(remaining, format);
        }
        if (this.remainingText) this.remainingText.style.display = 'block';
        if (this.fullText) this.fullText.style.display = 'none';
      } else {
        if (this.remainingText) this.remainingText.style.display = 'none';
        if (this.fullText) this.fullText.style.display = 'block';
      }
      this.style.setProperty('--percentage', percentage);
    }

    getFallbackMinimum() {
      return Math.round(parseInt(this.dataset.minimum, 10) * (Shopify.currency.rate || 1));
    }

    async fetchWithTimeout(resource, options = {}) {
      const { timeout = this.perfTimeoutMs } = options;
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeout);
      try {
        const response = await fetch(resource, { ...options, signal: controller.signal });
        clearTimeout(id);
        return response;
      } catch (e) {
        clearTimeout(id);
        throw e;
      }
    }

    formatMoney(cents, format) {
      if (typeof window.formatMoney === 'function') return window.formatMoney(cents, format);
      const value = (cents / 100).toFixed(2);
      return (format || '${{amount}}').replace('{{amount}}', value).replace('{{ amount }}', value);
    }

    cleanCache() {
      const now = Date.now();
      for (const [key, value] of this.cache.entries()) {
        if ((now - value.timestamp) >= this.cacheExpiry) {
          this.cache.delete(key);
        }
      }
    }

    clearCache() {
      this.cache.clear();
    }
  }
  customElements.define('free-shipping', FreeShipping);
}